'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { twMerge } from 'tailwind-merge';

type SliderRootBaseProps = React.ComponentProps<typeof SliderPrimitive.Root>;

type SliderProps = Omit<SliderRootBaseProps, 'onChange' | 'value' | 'defaultValue' | 'onValueChange'> & {
	value?: number | number[];
	defaultValue?: number | number[];
	onChange?: (event: number | number[], value: number | number[]) => void;
	marks?: boolean;
	variant?: 'default' | 'line';
	placeholder?: React.ReactNode;
	formatValue?: (value: number) => React.ReactNode;
};

const LARGURA_DO_THUMB = 2;
const FOLGA_DO_THUMB = 4;

function toArrayValue(v: SliderProps['value'] | SliderProps['defaultValue'], min: number, max: number) {
	if (Array.isArray(v)) return v;
	if (typeof v === 'number') return [v];
	return [min, max];
}

const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(function Slider(
	{ className, defaultValue, value, min = 0, max = 100, step = 1, onChange, marks, variant = 'default', placeholder, formatValue, disabled, ...props },
	ref,
) {
	const [internalValue, setInternalValue] = React.useState<number[]>(() => toArrayValue(value ?? defaultValue, min, max));

	React.useEffect(() => {
		if (value !== undefined) {
			setInternalValue(toArrayValue(value, min, max));
		}
	}, [value, min, max]);

	React.useEffect(() => {
		if (value === undefined) {
			setInternalValue(toArrayValue(defaultValue, min, max));
		}
	}, [defaultValue, min, max, value]);

	const handleValueChange = (vals: number[]) => {
		setInternalValue(vals);

		if (onChange) {
			const muiValue: number | number[] = vals.length === 1 ? vals[0] : vals;
			onChange(muiValue, muiValue);
		}
	};

	const controlledValue = value !== undefined ? toArrayValue(value, min, max) : undefined;

	const markValues = React.useMemo(() => {
		if (!marks) return [];
		const stepNum = typeof step === 'number' && step > 0 ? step : 1;
		const values: number[] = [];
		for (let v = min; v <= max; v = Number((v + stepNum).toFixed(10))) {
			values.push(v);
		}
		if (values[values.length - 1] !== max) values.push(max);
		return values;
	}, [marks, min, max, step]);

	const fracao = max === min ? 0 : Math.min(1, Math.max(0, (internalValue[internalValue.length - 1] - min) / (max - min)));
	const larguraDoPreenchimento = `calc(${fracao * 100}% + ${LARGURA_DO_THUMB * (1 - fracao) + FOLGA_DO_THUMB * 2}px)`;

	const comuns = {
		ref,
		'data-slot': 'slider',
		defaultValue: defaultValue !== undefined ? toArrayValue(defaultValue, min, max) : undefined,
		value: controlledValue,
		min,
		max,
		step,
		disabled,
		onValueChange: handleValueChange,
		...props,
	};

	if (variant === 'default') {
		return (
			<div
				className={twMerge(
					'flex w-full items-center rounded-lg border border-gray-200 bg-white !p-[2px] h-[36px]',
					disabled && 'pointer-events-none opacity-50',
					className,
				)}>
				<SliderPrimitive.Root {...comuns} className='slider-caixa-empreender relative flex h-full w-full touch-none select-none items-center cursor-ew-resize mx-1'>
					<SliderPrimitive.Track data-slot='slider-track' className='absolute inset-0 rounded-md h-[30px] '>
						{internalValue.length === 1 ? (
							<div data-slot='slider-range' className='absolute -left-1 bg-gray-200 rounded-md h-[30px]' style={{ width: larguraDoPreenchimento }} />
						) : (
							<SliderPrimitive.Range data-slot='slider-range' className='absolute bg-gray-200 rounded-md h-[30px]' />
						)}
					</SliderPrimitive.Track>

					<div className='pointer-events-none absolute inset-y-0 -left-1 -right-1 flex items-center justify-between gap-2 px-3'>
						<span className='truncate text-sm font-normal leading-5 text-gray-900'>{placeholder}</span>

						<span className='shrink-0 text-sm font-normal leading-5 tabular-nums text-gray-500'>
							{internalValue.map((v) => (formatValue ? formatValue(v) : v)).reduce<React.ReactNode[]>((acc, atual, i) => (i === 0 ? [atual] : [...acc, ' – ', atual]), [])}
						</span>
					</div>

					{internalValue.map((_, index) => (
						<SliderPrimitive.Thumb
							data-slot='slider-thumb'
							key={index}
							className='block h-[18px] w-[2px] shrink-0 rounded-full bg-transparent backdrop-invert !outline-none'
						/>
					))}
				</SliderPrimitive.Root>
			</div>
		);
	}

	return (
		<SliderPrimitive.Root
			{...comuns}
			className={twMerge(
				'slider-root-empreender relative flex w-full touch-none select-none items-center',
				'data-[orientation=horizontal]:h-5',
				'data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
				'data-[disabled]:opacity-50',
				marks && 'data-[orientation=horizontal]:mb-4',
				className,
			)}>
			<SliderPrimitive.Track
				data-slot='slider-track'
				className={twMerge(
					'relative grow overflow-hidden rounded-full bg-gray-200',
					'data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full',
					'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
				)}>
				<SliderPrimitive.Range
					data-slot='slider-range'
					className={twMerge('absolute bg-gray-900', 'data-[orientation=horizontal]:h-full', 'data-[orientation=vertical]:w-full')}
				/>
			</SliderPrimitive.Track>

			{marks &&
				markValues.map((mark) => {
					const pct = max === min ? 0 : ((mark - min) / (max - min)) * 100;
					return (
						<span
							key={mark}
							aria-hidden
							className='pointer-events-none absolute top-full mt-2 size-1 -translate-x-1/2 rounded-full bg-gray-300'
							style={{ left: `${pct}%` }}
						/>
					);
				})}

			{internalValue.map((_, index) => (
				<SliderPrimitive.Thumb
					data-slot='slider-thumb'
					key={index}
					className={twMerge(
						'block size-5 shrink-0 rounded-full border border-gray-900 bg-white shadow-sm !outline-none',
						'transition-[color,box-shadow] ring-gray-900/10 hover:ring-4',
						'disabled:pointer-events-none disabled:opacity-50',
					)}
				/>
			))}
		</SliderPrimitive.Root>
	);
});

export default Slider;
